import { useState, useRef, useEffect, useCallback } from "react";
import {
  RiBold,
  RiItalic,
  RiUnderline,
  RiLink,
  RiListUnordered,
  RiListOrdered,
  RiCodeLine,
  RiAttachment2,
  RiCloseLine,
  RiLoader4Line,
} from "react-icons/ri";
import Button from "../../Utility/Button/Button";
import MentionAutocomplete from "./MentionAutocomplete";
import DescriptionImageSizeModal from "../../Utility/DescriptionEditor/DescriptionImageSizeModal";
import { useEditorImageResize } from "../../Utility/DescriptionEditor/useEditorImageResize";
import { buildMentionSpanElement, filterMentionUsers, getActiveMentionQuery } from "./mentionUtils";
import CommentAttachmentChip from "./CommentAttachmentChip";
import { splitCommentAttachments } from "./commentAttachmentUtils";
import {
  CLIENT_ATTACHMENT_EXTENSIONS,
  CLIENT_ATTACHMENT_ACCEPTED_TYPES,
  validateUploadFiles,
} from "../../utils/fileUploadValidation";

function getTextBeforeCaret(editor) {
  if (!editor) return "";
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return "";
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return "";
  const preRange = range.cloneRange();
  preRange.selectNodeContents(editor);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString();
}

function getCaretClientRect() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0).cloneRange();
  range.collapse(false);
  const rects = range.getClientRects();
  if (rects.length > 0) {
    return rects[rects.length - 1];
  }
  const marker = document.createElement("span");
  marker.textContent = "\u200b";
  range.insertNode(marker);
  const rect = marker.getBoundingClientRect();
  marker.parentNode?.removeChild(marker);
  return rect;
}

/** DOM Node.TEXT_NODE — avoid `Node` global for eslint no-undef */
const TEXT_NODE = 3;

const EMPTY_FORMAT_STATE = {
  bold: false,
  italic: false,
  underline: false,
  insertUnorderedList: false,
  insertOrderedList: false,
};

function readFormatState(editor) {
  if (!editor) return EMPTY_FORMAT_STATE;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !editor.contains(selection.anchorNode)) {
    return EMPTY_FORMAT_STATE;
  }
  try {
    return {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
    };
  } catch {
    return EMPTY_FORMAT_STATE;
  }
}

function deleteTextBeforeCaret(editor, charCount) {
  if (!editor || charCount <= 0) return;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return;

  for (let i = 0; i < charCount; i += 1) {
    const r = selection.getRangeAt(0);
    if (!r.collapsed) {
      r.deleteContents();
      continue;
    }
    if (r.startOffset > 0) {
      r.setStart(r.startContainer, r.startOffset - 1);
      r.deleteContents();
      continue;
    }
    const node = r.startContainer;
    const prev =
      node.nodeType === TEXT_NODE
        ? node.previousSibling
        : (node.childNodes[r.startOffset - 1] ?? node.previousSibling);
    if (!prev) break;
    if (prev.nodeType === TEXT_NODE && prev.textContent) {
      prev.textContent = prev.textContent.slice(0, -1);
    } else if (prev.parentNode) {
      prev.parentNode.removeChild(prev);
    } else {
      break;
    }
  }
}

function insertListFallback(editor, ordered) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return;

  const list = document.createElement(ordered ? "ol" : "ul");
  const li = document.createElement("li");
  if (!range.collapsed) {
    li.appendChild(range.extractContents());
  } else {
    li.innerHTML = "<br>";
  }
  list.appendChild(li);
  range.deleteContents();
  range.insertNode(list);

  const caret = document.createRange();
  caret.selectNodeContents(li);
  caret.collapse(false);
  selection.removeAllRanges();
  selection.addRange(caret);
}

function ensureEditorCaret(editor) {
  editor.focus();
  const selection = window.getSelection();
  if (!selection) return;
  if (selection.rangeCount === 0 || !editor.contains(selection.anchorNode)) {
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

const IMAGE_MIME_BY_EXTENSION = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  svg: "image/svg+xml",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif",
  tif: "image/tiff",
  tiff: "image/tiff",
};

/**
 * Image MIME for a dropped file, or "" when it isn't an image. Plenty of drag sources hand over
 * a File with an empty `type` (Finder aliases, some apps, older Safari), so falling back to the
 * extension is what keeps those from silently becoming attachments instead of inline images.
 */
function imageMimeForFile(file) {
  const type = String(file?.type ?? "");
  if (type.startsWith("image/")) return type;
  const extension = String(file?.name ?? "")
    .split(".")
    .pop()
    ?.toLowerCase();
  return IMAGE_MIME_BY_EXTENSION[extension] ?? "";
}

function isImageFile(file) {
  return Boolean(imageMimeForFile(file));
}

function readFileAsImageDataUrl(file) {
  const mime = imageMimeForFile(file);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      if (!result || result.startsWith("data:image/")) return resolve(result);
      // A File with no MIME type reads back as `data:;base64,…` / octet-stream, which renders as
      // a broken image and isn't recognisable as one server-side. Stamp the real type on it.
      resolve(mime ? result.replace(/^data:[^;]*;/, `data:${mime};`) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error(`Couldn't read "${file.name}"`));
    reader.readAsDataURL(file);
  });
}

/**
 * Where a dropped image should land. Prefers the caret under the pointer so the image goes
 * where it was dropped; falls back to the end of the editor when the browser can't resolve a
 * caret there (or the point is outside the editable area, e.g. a drop onto the toolbar).
 */
function resolveDropRange(editor, x, y) {
  let range = null;
  if (x != null && y != null) {
    if (typeof document.caretRangeFromPoint === "function") {
      range = document.caretRangeFromPoint(x, y);
    } else if (typeof document.caretPositionFromPoint === "function") {
      const position = document.caretPositionFromPoint(x, y);
      if (position?.offsetNode) {
        range = document.createRange();
        range.setStart(position.offsetNode, position.offset);
        range.collapse(true);
      }
    }
  }
  if (range && editor.contains(range.startContainer)) return range;
  const fallback = document.createRange();
  fallback.selectNodeContents(editor);
  fallback.collapse(false);
  return fallback;
}

/** Put the caret at `range` when it is still usable, else at the end of the editor. */
function restoreCaret(editor, range) {
  editor.focus();
  const selection = window.getSelection();
  if (selection && range) {
    try {
      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      /* range went stale between drop and insert — ensureEditorCaret recovers below */
    }
  }
  ensureEditorCaret(editor);
}

/** Direct-DOM insert, used when execCommand is unavailable or declines the edit. */
function insertImageNode(editor, src) {
  const img = document.createElement("img");
  img.src = src;
  const selection = window.getSelection();
  const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
  if (!range || !editor.contains(range.startContainer)) {
    editor.appendChild(img);
    return;
  }
  range.deleteContents();
  range.insertNode(img);
  range.setStartAfter(img);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Insert images at the caret, preferring the same execCommand path the toolbar buttons use —
 * it owns caret placement and the undo stack, so it behaves like the browser's own paste.
 * @returns whether the editor actually gained an image.
 */
function insertImagesAtCaret(editor, range, dataUrls) {
  if (dataUrls.length === 0) return false;
  const before = editor.querySelectorAll("img").length;
  restoreCaret(editor, range);

  dataUrls.forEach((dataUrl) => {
    let applied;
    try {
      applied = document.execCommand("insertHTML", false, `<img src="${dataUrl}">`);
    } catch {
      applied = false;
    }
    if (!applied) insertImageNode(editor, dataUrl);
  });

  return editor.querySelectorAll("img").length > before;
}

const MiniEditor = ({
  placeholder = "Add a comment…",
  onSubmit,
  onCancel,
  value = "",
  onChange,
  showToolbar = true,
  /** Comment edit mode: pinned toolbar, Save/Cancel, keep content after submit. */
  isEditMode = false,
  showActions = true,
  minHeight = "80px",
  disabled = false,
  /** Enable @mention when non-empty: [{ id, label }] */
  mentionUsers = [],
  mentionPlaceholder,
  /** Description field: click pasted images to preview and resize. */
  enableImageResize = false,
  /** Comments: attach-file button + drag-and-drop, separate from inline pasted images. */
  attachmentsEnabled = false,
  /** Edit mode: already-persisted attachments to render as chips. */
  existingAttachments = [],
  /** Present ⇒ existing attachment chips get a remove button. */
  onRemoveExistingAttachment,
  /** id of an existing attachment currently being removed (busy state). */
  removingAttachmentId = null,
  /** Max files per single attach/drop action (matches backend's per-upload-request limit). */
  maxAttachmentFiles = 10,
  maxAttachmentSizeMB = 10,
  /** Max attachments total on one comment (existing + pending), matches backend limit. */
  maxTotalAttachments = 50,
  /** Resolved view URLs (by attachment id) for existing pasted-image attachments — same map ActivityTabs resolves for the read-only list. */
  pastedImageUrls = {},
}) => {
  const editorRef = useRef(null);
  const wrapperRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const charsToDeleteRef = useRef(0);
  const [content, setContent] = useState(value || "");
  const [isFocused, setIsFocused] = useState(isEditMode);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [mentionPosition, setMentionPosition] = useState(null);
  const [charsToDeleteBeforeMention, setCharsToDeleteBeforeMention] = useState(0);
  const [formatState, setFormatState] = useState(EMPTY_FORMAT_STATE);
  const [editorRoot, setEditorRoot] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [existingImagePreviewSrc, setExistingImagePreviewSrc] = useState(null);

  const mentionsEnabled = Array.isArray(mentionUsers) && mentionUsers.length > 0;
  const filteredMentionUsers = mentionsEnabled
    ? filterMentionUsers(mentionUsers, mentionQuery)
    : [];

  const syncContentFromEditor = useCallback(() => {
    const html = editorRef.current?.innerHTML || "";
    setContent(html);
    onChange?.(html);
  }, [onChange]);

  const {
    imageModalOpen,
    imageModalSrc,
    imageModalWidthPercent,
    closeImageModal,
    applyImageWidth,
  } = useEditorImageResize(editorRoot, {
    enabled: enableImageResize && !disabled,
    onContentSync: syncContentFromEditor,
  });

  useEffect(() => {
    if (!enableImageResize) {
      setEditorRoot(null);
      return undefined;
    }
    const frameId = window.requestAnimationFrame(() => {
      setEditorRoot(editorRef.current);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [enableImageResize, value]);

  const refreshFormatState = useCallback(() => {
    setFormatState(readFormatState(editorRef.current));
  }, []);

  const closeMention = useCallback(() => {
    setMentionOpen(false);
    setMentionQuery("");
    setMentionActiveIndex(0);
    setMentionPosition(null);
    setCharsToDeleteBeforeMention(0);
  }, []);

  const updateMentionState = useCallback(() => {
    if (!mentionsEnabled || !editorRef.current) {
      closeMention();
      return;
    }
    const textBefore = getTextBeforeCaret(editorRef.current);
    const active = getActiveMentionQuery(textBefore);
    if (!active) {
      closeMention();
      return;
    }
    const deleteCount = active.query.length + 1;
    charsToDeleteRef.current = deleteCount;
    setCharsToDeleteBeforeMention(deleteCount);
    setMentionQuery((prevQuery) => {
      const nextQuery = active.query;
      if (prevQuery !== nextQuery) {
        setMentionActiveIndex(0);
      }
      return nextQuery;
    });
    setMentionOpen(true);
    const rect = getCaretClientRect();
    if (rect) {
      setMentionPosition({
        top: rect.top,
        left: rect.left,
        placement: "above",
      });
    }
  }, [mentionsEnabled, closeMention]);

  const insertMention = useCallback(
    (user) => {
      const editor = editorRef.current;
      if (!editor || !user?.id) return;
      editor.focus();
      deleteTextBeforeCaret(editor, charsToDeleteRef.current || charsToDeleteBeforeMention);

      const selection = window.getSelection();
      if (!selection) return;
      const range =
        selection.rangeCount > 0
          ? selection.getRangeAt(0)
          : (() => {
              const r = document.createRange();
              r.selectNodeContents(editor);
              r.collapse(false);
              return r;
            })();

      const span = buildMentionSpanElement(user.id, user.label);
      const space = document.createTextNode("\u00a0");
      range.insertNode(span);
      range.setStartAfter(span);
      range.collapse(true);
      range.insertNode(space);
      range.setStartAfter(space);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);

      closeMention();
      syncContentFromEditor();
      refreshFormatState();
    },
    [charsToDeleteBeforeMention, closeMention, syncContentFromEditor, refreshFormatState]
  );

  // Sync with external value prop (controlled mode)
  useEffect(() => {
    if (value !== undefined && editorRef.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || "";
        setContent(value || "");
      }
      refreshFormatState();
    }
  }, [value, refreshFormatState]);

  useEffect(() => {
    const handleSelectionChange = () => {
      const editor = editorRef.current;
      if (!editor) return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || !editor.contains(selection.anchorNode)) {
        return;
      }
      refreshFormatState();
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [refreshFormatState]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleInput = () => {
      syncContentFromEditor();
      updateMentionState();
      refreshFormatState();
    };

    const handleFocus = () => {
      setIsFocused(true);
      refreshFormatState();
    };
    const handleBlur = () => {
      if (isEditMode) return;
      setTimeout(() => {
        setIsFocused(false);
        setFormatState(EMPTY_FORMAT_STATE);
        closeMention();
      }, 200);
    };

    editor.addEventListener("input", handleInput);
    editor.addEventListener("focus", handleFocus);
    editor.addEventListener("blur", handleBlur);

    return () => {
      editor.removeEventListener("input", handleInput);
      editor.removeEventListener("focus", handleFocus);
      editor.removeEventListener("blur", handleBlur);
    };
  }, [syncContentFromEditor, updateMentionState, closeMention, isEditMode, refreshFormatState]);

  const showFormattingToolbar = showToolbar && (isFocused || isEditMode);

  const runEditorCommand = useCallback(
    (command, value = null) => {
      const editor = editorRef.current;
      if (!editor) return;

      ensureEditorCaret(editor);

      if (command === "insertUnorderedList" || command === "insertOrderedList") {
        try {
          document.execCommand("defaultParagraphSeparator", false, "div");
        } catch {
          /* ignore unsupported browsers */
        }
      }

      const applied = document.execCommand(command, false, value);
      if (!applied && (command === "insertUnorderedList" || command === "insertOrderedList")) {
        insertListFallback(editor, command === "insertOrderedList");
      }

      syncContentFromEditor();
      refreshFormatState();
    },
    [syncContentFromEditor, refreshFormatState]
  );

  const handleToolbarMouseDown = (e, command, value = null) => {
    e.preventDefault();
    runEditorCommand(command, value);
  };

  const addFiles = useCallback(
    async (fileList) => {
      if (!attachmentsEnabled) return;
      const incoming = Array.from(fileList || []).filter(Boolean);
      if (incoming.length === 0) return;

      setAttachmentError("");
      if (incoming.length > maxAttachmentFiles) {
        setAttachmentError(`Maximum ${maxAttachmentFiles} files per attach/drop`);
        return;
      }
      if (
        existingAttachments.length + pendingFiles.length + incoming.length >
        maxTotalAttachments
      ) {
        setAttachmentError(`Maximum ${maxTotalAttachments} attachments allowed on this comment`);
        return;
      }

      const maxSizeBytes = maxAttachmentSizeMB * 1024 * 1024;
      const deduped = [];
      for (const file of incoming) {
        if (file.size > maxSizeBytes) {
          setAttachmentError(`File "${file.name}" exceeds ${maxAttachmentSizeMB}MB limit`);
          return;
        }
        const isDuplicate =
          pendingFiles.some((f) => f.name === file.name && f.size === file.size) ||
          deduped.some((f) => f.name === file.name && f.size === file.size);
        if (!isDuplicate) deduped.push(file);
      }
      if (deduped.length === 0) {
        setAttachmentError(
          incoming.length === 1
            ? `"${incoming[0].name}" is already attached`
            : "Those files are already attached"
        );
        return;
      }

      const validationError = await validateUploadFiles(deduped, {
        inspectContent: false,
        allowedExtensions: CLIENT_ATTACHMENT_EXTENSIONS,
      });
      if (validationError) {
        setAttachmentError(validationError);
        return;
      }

      setPendingFiles((prev) => [...prev, ...deduped]);
    },
    [
      attachmentsEnabled,
      pendingFiles,
      existingAttachments.length,
      maxAttachmentFiles,
      maxAttachmentSizeMB,
      maxTotalAttachments,
    ]
  );

  /**
   * Drop an image → inline in the comment body, exactly as a clipboard paste does. The backend
   * extracts `<img>` data URIs out of the submitted HTML and stores them as "pasted-image-N.ext",
   * which is what makes them render inline instead of as a file chip.
   */
  const insertImageFiles = useCallback(
    async (imageFiles, dropRange) => {
      const editor = editorRef.current;
      if (!editor || imageFiles.length === 0) return;

      setAttachmentError("");
      const maxSizeBytes = maxAttachmentSizeMB * 1024 * 1024;
      const oversized = imageFiles.find((file) => file.size > maxSizeBytes);
      if (oversized) {
        setAttachmentError(`File "${oversized.name}" exceeds ${maxAttachmentSizeMB}MB limit`);
        return;
      }

      const validationError = await validateUploadFiles(imageFiles, {
        inspectContent: false,
        allowedExtensions: CLIENT_ATTACHMENT_EXTENSIONS,
      });
      if (validationError) {
        setAttachmentError(validationError);
        return;
      }

      // `dropRange` was resolved synchronously on drop; Ranges track DOM edits, so it still
      // points at the drop position after the async read.
      let inserted;
      try {
        const dataUrls = (await Promise.all(imageFiles.map(readFileAsImageDataUrl))).filter(
          Boolean
        );
        inserted = insertImagesAtCaret(editor, dropRange, dataUrls);
      } catch {
        inserted = false;
      }

      // Inlining depends on caret/selection APIs that can decline the edit depending on where
      // the drop landed. Rather than leave the drop as a no-op — no image, no chip, and a dead
      // Comment button — fall back to attaching the file so the comment is still submittable.
      if (!inserted) {
        addFiles(imageFiles);
        return;
      }

      syncContentFromEditor();
      // A programmatic DOM edit fires no `input` event, so anything listening for one (the
      // editor's own handler, and any future listener) would miss the image. Dispatch it so a
      // drop travels the identical path a paste does.
      editor.dispatchEvent(new Event("input", { bubbles: true }));
    },
    [maxAttachmentSizeMB, syncContentFromEditor, addFiles]
  );

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError("");
  };

  const handleAttachButtonClick = () => {
    attachmentInputRef.current?.click();
  };

  const handleAttachInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const handleEditorDragEnter = (e) => {
    if (!attachmentsEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(true);
  };

  const handleEditorDragOver = (e) => {
    if (!attachmentsEnabled) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEditorDragLeave = (e) => {
    if (!attachmentsEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
  };

  const handleEditorDrop = (e) => {
    if (!attachmentsEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
    // A drag originating from a web page or another app often carries only a URL/HTML
    // reference with no File behind it, so `files` is empty. Say so instead of ignoring the
    // drop, which left no chip, no error, and a still-disabled Comment button.
    const dropped = Array.from(e.dataTransfer?.files ?? []).filter(Boolean);
    if (dropped.length === 0) {
      setAttachmentError(
        "Couldn't read the dropped image. Drop a file from Finder, or copy and paste the image instead."
      );
      return;
    }
    // Images go inline like a paste; everything else still becomes an attachment chip.
    const images = dropped.filter(isImageFile);
    const files = dropped.filter((file) => !isImageFile(file));
    if (images.length > 0) {
      const editor = editorRef.current;
      if (editor) insertImageFiles(images, resolveDropRange(editor, e.clientX, e.clientY));
    }
    if (files.length > 0) addFiles(files);
  };

  const handleLinkMouseDown = (e) => {
    e.preventDefault();
    const editor = editorRef.current;
    if (!editor) return;
    ensureEditorCaret(editor);
    const url = prompt("Enter URL:");
    if (url) {
      runEditorCommand("createLink", url);
    }
  };

  // Existing (already-persisted) attachments count too — clearing all text on an edit that
  // still has an attachment (new or existing) must stay saveable, not just brand-new pending files.
  const hasAnyAttachments =
    attachmentsEnabled && (pendingFiles.length > 0 || existingAttachments.length > 0);

  // An image-only comment has no text at all, so `textContent` — the fallback that catches a
  // `content` state that drifted from the DOM — has to count inline images too, or a comment
  // whose whole body is a pasted/dropped image looks empty and stays unsubmittable.
  const hasEditorContent = () => {
    if (content.trim()) return true;
    const editor = editorRef.current;
    if (!editor) return false;
    return Boolean(editor.textContent?.trim() || editor.querySelector("img"));
  };

  const canSubmit = hasEditorContent() || hasAnyAttachments;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    const htmlContent = editorRef.current?.innerHTML || "";
    const textContent = editorRef.current?.textContent || "";
    closeMention();

    // Wait for the parent to persist before clearing. Clearing optimistically threw away the
    // user's text and pending attachments even when the upload failed, so a rejected file
    // looked like the button had silently done nothing. `false` means "did not persist";
    // consumers that return nothing are treated as success, preserving previous behaviour.
    let persisted = true;
    if (onSubmit) {
      try {
        const result = await onSubmit({
          html: htmlContent,
          text: textContent,
          ...(attachmentsEnabled ? { files: pendingFiles } : {}),
        });
        persisted = result !== false;
      } catch {
        persisted = false;
      }
    }
    if (!persisted) return;

    if (!isEditMode && editorRef.current) {
      editorRef.current.innerHTML = "";
      setContent("");
      setFormatState(EMPTY_FORMAT_STATE);
      if (attachmentsEnabled) {
        setPendingFiles([]);
        setAttachmentError("");
      }
    }
  };

  const isMentionPickerActive = mentionOpen && filteredMentionUsers.length > 0;

  const handleKeyDown = (e) => {
    if (isMentionPickerActive) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setMentionActiveIndex((i) => (i + 1) % filteredMentionUsers.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setMentionActiveIndex(
          (i) => (i - 1 + filteredMentionUsers.length) % filteredMentionUsers.length
        );
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        insertMention(filteredMentionUsers[mentionActiveIndex]);
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredMentionUsers[mentionActiveIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeMention();
        return;
      }
    } else if (mentionOpen && e.key === "Escape") {
      e.preventDefault();
      closeMention();
      return;
    }
  };

  const editorPlaceholder =
    mentionPlaceholder ??
    (mentionsEnabled ? `${placeholder} Type @ to mention someone` : placeholder);

  return (
    <div
      className={`mini-editor${isDraggingFiles ? " mini-editor-drop-active" : ""}`}
      ref={wrapperRef}
      onDragEnter={attachmentsEnabled ? handleEditorDragEnter : undefined}
      onDragOver={attachmentsEnabled ? handleEditorDragOver : undefined}
      onDragLeave={attachmentsEnabled ? handleEditorDragLeave : undefined}
      onDrop={attachmentsEnabled ? handleEditorDrop : undefined}
    >
      {showFormattingToolbar && (
        <div className="mini-editor-toolbar">
          <Button
            type="button"
            variant="icon"
            size="vsm"
            title="Bold (Ctrl+B)"
            onMouseDown={(e) => handleToolbarMouseDown(e, "bold")}
            className={`toolbar-btn ${formatState.bold ? "active" : ""}`}
          >
            <RiBold className="text-base" />
          </Button>

          <Button
            type="button"
            variant="icon"
            size="vsm"
            onMouseDown={(e) => handleToolbarMouseDown(e, "italic")}
            className={`toolbar-btn ${formatState.italic ? "active" : ""}`}
            title="Italic (Ctrl+I)"
          >
            <RiItalic className="text-base" />
          </Button>

          <Button
            type="button"
            variant="icon"
            size="vsm"
            onMouseDown={(e) => handleToolbarMouseDown(e, "underline")}
            className={`toolbar-btn ${formatState.underline ? "active" : ""}`}
            title="Underline (Ctrl+U)"
          >
            <RiUnderline className="text-base" />
          </Button>

          <div className="toolbar-divider" />

          <Button
            type="button"
            variant="icon"
            size="vsm"
            onMouseDown={handleLinkMouseDown}
            className="toolbar-btn"
            title="Insert Link"
          >
            <RiLink className="text-base" />
          </Button>

          <Button
            type="button"
            variant="icon"
            size="vsm"
            onMouseDown={(e) => handleToolbarMouseDown(e, "insertUnorderedList")}
            className={`toolbar-btn ${formatState.insertUnorderedList ? "active" : ""}`}
            title="Bullet List"
          >
            <RiListUnordered className="text-base" />
          </Button>

          <Button
            type="button"
            variant="icon"
            size="vsm"
            onMouseDown={(e) => handleToolbarMouseDown(e, "insertOrderedList")}
            className={`toolbar-btn ${formatState.insertOrderedList ? "active" : ""}`}
            title="Numbered List"
          >
            <RiListOrdered className="text-base" />
          </Button>

          <Button
            type="button"
            variant="icon"
            size="vsm"
            onMouseDown={(e) => handleToolbarMouseDown(e, "formatBlock", "pre")}
            className="toolbar-btn"
            title="Code Block"
          >
            <RiCodeLine className="text-base" />
          </Button>

          {attachmentsEnabled && (
            <Button
              type="button"
              variant="icon"
              size="vsm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleAttachButtonClick}
              className="toolbar-btn"
              title="Attach file"
            >
              <RiAttachment2 className="text-base" />
            </Button>
          )}
        </div>
      )}

      {attachmentsEnabled && (
        <input
          ref={attachmentInputRef}
          type="file"
          multiple
          hidden
          accept={`${CLIENT_ATTACHMENT_ACCEPTED_TYPES},image/*`}
          onChange={handleAttachInputChange}
        />
      )}

      <div
        ref={editorRef}
        contentEditable={!disabled}
        className="mini-editor-content"
        data-placeholder={editorPlaceholder}
        onKeyDown={handleKeyDown}
        onKeyUp={
          mentionsEnabled
            ? (e) => {
                if (e.key === "ArrowDown" || e.key === "ArrowUp") return;
                updateMentionState();
              }
            : undefined
        }
        style={{ minHeight }}
        suppressContentEditableWarning
      />

      {(() => {
        if (!attachmentsEnabled) return null;
        const { pastedImages: existingPastedImages, other: otherExistingAttachments } =
          splitCommentAttachments(existingAttachments);
        const hasAnything =
          existingAttachments.length > 0 || pendingFiles.length > 0 || attachmentError;
        if (!hasAnything) return null;
        return (
          <div className="mini-editor-attachments-wrap">
            {attachmentError ? (
              <p className="mini-editor-attachment-error">{attachmentError}</p>
            ) : null}

            {existingPastedImages.length > 0 && (
              <div className="comment-pasted-images">
                {existingPastedImages.map((attachment) => {
                  const attachmentId = attachment?.id != null ? String(attachment.id) : "";
                  const url = attachmentId ? pastedImageUrls[attachmentId] : null;
                  const isRemoving = attachmentId !== "" && removingAttachmentId === attachmentId;
                  if (!url) return null;
                  return (
                    <div key={attachmentId} className="comment-pasted-image-editable">
                      <img
                        src={url}
                        alt={attachment?.name ?? "Pasted image"}
                        className="comment-pasted-image-preview"
                        onClick={() => setExistingImagePreviewSrc(url)}
                      />
                      {onRemoveExistingAttachment ? (
                        <button
                          type="button"
                          className="comment-pasted-image-remove"
                          aria-label={`Remove ${attachment?.name ?? "pasted image"}`}
                          disabled={isRemoving}
                          onClick={() => onRemoveExistingAttachment(attachment)}
                        >
                          {isRemoving ? (
                            <RiLoader4Line className="w-4 h-4 animate-spin" aria-hidden />
                          ) : (
                            <RiCloseLine className="w-4 h-4" aria-hidden />
                          )}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            {otherExistingAttachments.length > 0 || pendingFiles.length > 0 ? (
              <div className="mini-editor-attachments">
                {otherExistingAttachments.map((attachment) => {
                  const attachmentId = attachment?.id != null ? String(attachment.id) : "";
                  const name =
                    attachment?.name ??
                    attachment?.originalName ??
                    attachment?.fileName ??
                    "Attachment";
                  const mimeType = attachment?.mimeType ?? attachment?.contentType ?? "";
                  return (
                    <CommentAttachmentChip
                      key={attachmentId || name}
                      name={name}
                      isImage={String(mimeType).startsWith("image/")}
                      previewUrl={attachment?.thumbnailUrl ?? null}
                      onRemove={
                        onRemoveExistingAttachment
                          ? () => onRemoveExistingAttachment(attachment)
                          : undefined
                      }
                      busy={attachmentId !== "" && removingAttachmentId === attachmentId}
                    />
                  );
                })}
                {pendingFiles.map((file, index) => (
                  <CommentAttachmentChip
                    key={`${file.name}-${file.size}-${index}`}
                    file={file}
                    onRemove={() => removePendingFile(index)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })()}

      {mentionsEnabled && (
        <MentionAutocomplete
          open={isMentionPickerActive}
          users={filteredMentionUsers}
          activeIndex={mentionActiveIndex}
          onSelect={insertMention}
          onHighlight={setMentionActiveIndex}
          position={mentionPosition}
        />
      )}

      {showActions && (
        <div className="mini-editor-actions">
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onCancel}
              className="!px-3 !py-1.5"
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={disabled || !canSubmit}
            className="!px-3 !py-1.5"
          >
            {disabled ? (isEditMode ? "Saving…" : "Adding…") : isEditMode ? "Save" : "Comment"}
          </Button>
        </div>
      )}

      {enableImageResize ? (
        <DescriptionImageSizeModal
          isOpen={imageModalOpen}
          src={imageModalSrc}
          widthPercent={imageModalWidthPercent}
          onClose={closeImageModal}
          onApply={applyImageWidth}
        />
      ) : null}

      {attachmentsEnabled ? (
        <DescriptionImageSizeModal
          isOpen={!!existingImagePreviewSrc}
          src={existingImagePreviewSrc}
          onClose={() => setExistingImagePreviewSrc(null)}
          readOnly
        />
      ) : null}
    </div>
  );
};

export default MiniEditor;
